import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifySession, saveSession, getSessionToken, clearSession, isSessionGateEnabled } from '../services/sessionService';

/**
 * SessionGate component
 *
 * Wraps public-facing pages to enforce short-lived session access.
 * If a valid session token is found in the URL query parameter (?session=xxx),
 * it saves the token in localStorage (shared across tabs) and allows access.
 * If no valid session exists, it shows an access denied message.
 *
 * The feature can be globally toggled on/off by admin via the session-config API.
 * When disabled, the gate simply passes through all requests.
 *
 * Admin pages (which use ProtectedRoute) are NOT wrapped by this.
 *
 * Usage:
 *   <SessionGate>
 *     <PublicPage />
 *   </SessionGate>
 */
const SessionGate = ({ children }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const mountedRef = useRef(true);
  const [sessionState, setSessionState] = useState({
    checking: true,
    granted: false,
    reason: null,
    gateEnabled: true,
  });

  // Use ref to read params without triggering re-creation of attemptAccess
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // Track if we've already verified for this page load to avoid re-verifying
  // after URL cleanup (which would change searchParams and re-trigger the effect)
  const verifiedRef = useRef(false);

  const attemptAccess = useCallback(async () => {
    // Prevent re-entry if already verified
    if (verifiedRef.current) return;
    verifiedRef.current = true;

    const params = searchParamsRef.current;

    // Step 0: Check if the session gate feature is globally enabled
    const gateEnabled = await isSessionGateEnabled();

    if (!mountedRef.current) return;
    
    if (!gateEnabled) {
      // Feature is disabled — allow all access
      setSessionState({ checking: false, granted: true, reason: null, gateEnabled: false });
      // Clear any stale session data (including scope) so filtering doesn't apply
      clearSession();
      // Clean ?session= from URL if present
      if (params.get('session')) {
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
      return;
    }

    // Step 1: Check URL for ?session= token
    const urlToken = params.get('session');

    // Step 2: Fallback to localStorage (shared across tabs)
    const storedToken = getSessionToken();
    const token = urlToken || storedToken;

    if (!token) {
      setSessionState({ checking: false, granted: false, reason: 'no_token', gateEnabled: true });
      return;
    }

    // Step 3: Verify token with backend
    const result = await verifySession(token);

    if (!mountedRef.current) return;

    if (result.valid) {
      // Save valid token
      saveSession(token, result.session);
      setSessionState({ checking: false, granted: true, reason: null, gateEnabled: true });

      // Clean URL with optional redirect to the session's landing_path
      if (urlToken) {
        const landingPath = result.landing_path || '/';
        // Only redirect if the current page doesn't already match the landing path
        if (window.location.pathname !== landingPath) {
          window.history.replaceState({}, '', landingPath + window.location.hash);
        } else {
          // Just clean the session param from URL
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', newUrl);
        }
      }
    } else {
      clearSession();
      setSessionState({ checking: false, granted: false, reason: result.reason || 'invalid', gateEnabled: true });
    }
  }, []); // No dependencies — reads refs instead

  // Run once on mount only
  useEffect(() => {
    attemptAccess();

    return () => {
      mountedRef.current = false;
    };
  }, [attemptAccess]);

  // While checking
  if (sessionState.checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">{t('session.verifying')}</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (!sessionState.granted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {sessionState.reason === 'no_token'
              ? t('session.noTokenTitle')
              : t('session.accessDeniedTitle')}
          </h2>
          <p className="text-gray-600 mb-6">
            {sessionState.reason === 'no_token'
              ? t('session.noTokenMessage')
              : sessionState.reason === 'Session expired'
                ? t('session.expiredMessage')
                : t('session.accessDeniedMessage')}
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-500">
              {t('session.contactAdmin')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Access granted — render children
  return <>{children}</>;
};

export default SessionGate;